"use client";

// React Imports
import {useState, useCallback} from "react";

// React native Imports
import {Text} from "react-native";

// Next Imports
import {signIn} from "next-auth/react";

// MUI Imports
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";

// Third-party Imports
import * as WebBrowser from "expo-web-browser";
import * as Linking from "expo-linking";
import {setCookie} from "cookies-next/client";

// Type Imports
import type {Mode} from "@core/types";

// Component Imports
import Logo from "@components/layout/shared/Logo";
import Loading from "@views/loading";
import LanguageDropdown from "@components/layout/shared/LanguageDropdown";

// Config Imports
import themeConfig from "@configs/themeConfig";

const SSO_BTN = {
  justifyContent: "center",
  paddingLeft: "16px",
  paddingRight: "16px",
  paddingTop: "10px",
  paddingBottom: "10px",
  borderRadius: "8px",
  border: "1px solid #e0e0e0",
  backgroundColor: "#fff",
  color: "#333",
  boxShadow: "none",
  fontSize: "0.875rem",
  fontWeight: 400,
  textTransform: "none" as const,
  gap: "8px",
  "& .MuiButton-startIcon": {marginRight: "6px", marginLeft: 0},
  "&:hover": {backgroundColor: "#f5f7fa", boxShadow: "none", borderColor: "#d0d0d0"},
} as const;

const MicrosoftIcon = () => (
  <svg xmlns='http://www.w3.org/2000/svg' width='18' height='18' viewBox='0 0 23 23'>
    <path fill='#f35325' d='M1 1h10v10H1z'/>
    <path fill='#81bc06' d='M12 1h10v10H12z'/>
    <path fill='#05a6f0' d='M1 12h10v10H1z'/>
    <path fill='#ffba08' d='M12 12h10v10H12z'/>
  </svg>
);

const SurfconextIcon = () => (
  <svg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 100 100'>
    <circle cx='50' cy='50' r='50' fill='#009de0'/>
    <path d='M24 62 Q38 30 50 50 Q62 70 76 38' stroke='#fff' strokeWidth='8' fill='none' strokeLinecap='round'/>
  </svg>
);

const KennisnetIcon = () => (
  <svg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 100 100'>
    <rect width='100' height='100' rx='12' fill='#e4022d'/>
    <text x='50' y='68' textAnchor='middle' fontSize='52' fontWeight='bold' fill='#fff' fontFamily='Arial,sans-serif'>K</text>
  </svg>
);

const LoginV2 = ({mode, dictionary}: {mode: Mode; dictionary: any}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const handleAuthResult = useCallback(async (result: any, provider: string) => {
    // eslint-disable-next-line eqeqeq
    if (result && result.type == "success") {
      const search_params = new URL(result.url).searchParams;
      if (search_params.get("error")) {
        setError(search_params.get("error") ?? "");
        throw search_params.get("error");
      }
      setCookie("NEXT_LOCALE", search_params.get("lang"), {path: "/"});
      const signInResult = await signIn("credentials", {
        id: search_params.get("id"),
        avatar: search_params.get("avatar"),
        name: search_params.get("name"),
        email: search_params.get("email"),
        lang: search_params.get("lang"),
        permission: search_params.get("permission"),
        sessionId: search_params.get("sessionId"),
        redirect: false,
      });
      if (signInResult?.ok) {
        window.location.href = `/${search_params.get("lang") ?? "en"}/dashboard`;
      } else {
        setError("login_failed");
        setIsLoading(false);
      }
    } else {
      setIsLoading(false);
    }
  }, []);

  const doOpenWebBrowser = useCallback(async (federationName: string) => {
    setIsLoading(true);
    setError("");
    try {
      const result: any = await WebBrowser.openAuthSessionAsync(
        `${process.env.NEXT_PUBLIC_API_URL}/auth/sso/init/?linkingUri=${Linking.createURL("/?")}&federation=${federationName}`
      );
      await handleAuthResult(result, federationName);
    } catch (err) {
      console.error(`[Login - ${federationName}] Error:`, err);
      setIsLoading(false);
    }
  }, [handleAuthResult]);

  const doOpenAzureWebBrowser = useCallback(async () => {
    setIsLoading(true);
    setError("");
    try {
      const result: any = await WebBrowser.openAuthSessionAsync(`${process.env.NEXT_PUBLIC_APP_URL}/azure`);
      await handleAuthResult(result, "Azure");
    } catch (err) {
      console.error("[Login - Azure] Error:", err);
      setIsLoading(false);
    }
  }, [handleAuthResult]);

  return (
    <>
      <style>{`
        .login-v2-root { display: flex; min-height: 100dvh; width: 100%; color-scheme: light; }
        .login-v2-root, .login-v2-root * { forced-color-adjust: none; }
        .login-v2-left {
          position: relative; display: flex; flex-direction: column;
          width: 50%; min-width: 400px; max-width: unset;
          min-height: 100dvh; background: #fff; flex-shrink: 0;
        }
        .login-v2-right {
          flex: 1; min-height: 100dvh;
          background: radial-gradient(ellipse at 20% 15%, #fafcff 0%, #e4eefb 45%, #8fb3e8 100%);
          display: flex; flex-direction: column;
          justify-content: center; align-items: center;
          padding: 60px 48px; gap: 32px;
          position: relative; overflow: hidden;
        }
        .login-v2-form-wrap {
          flex: 1; display: flex; flex-direction: column;
          justify-content: center; align-items: center; padding: 0 48px; gap: 0;
        }
        .login-v2-form-inner { width: 100%; max-width: 440px; }
        .login-v2-footer { padding: 0 48px 36px; text-align: center; }
        @media (max-width: 1200px) {
          .login-v2-left { width: 100%; max-width: 100%; min-width: unset; }
          .login-v2-right { display: none; }
          .login-v2-form-wrap { padding: 0 32px; }
          .login-v2-footer { padding: 0 32px 32px; }
          .login-v2-logo { padding: 24px 24px 0 !important; }
          .login-v2-lang { top: 20px !important; right: 16px !important; }
        }
        @media (max-width: 480px) {
          .login-v2-form-wrap { padding: 0 20px; }
          .login-v2-footer { padding: 0 20px 24px; }
          .login-v2-logo { padding: 20px 20px 0 !important; }
        }
      `}</style>

      <div className='login-v2-root'>

        {/* ── LEFT PANEL ── */}
        <div className='login-v2-left'>
          {/* Logo */}
          <div className='login-v2-logo' style={{padding: "36px 48px 0"}}>
            <Logo />
          </div>

          {/* Language picker */}
          <div className='login-v2-lang' style={{position: "absolute", top: 28, right: 20}}>
            <LanguageDropdown dictionary={dictionary} />
          </div>

          {/* Centered form */}
          <div className='login-v2-form-wrap'>
            <div className='login-v2-form-inner'>
              <Typography sx={{fontSize: "1.4rem", fontWeight: 700, color: "#111", mb: 0.75}}>
                {dictionary["auth"].login_title || "Sign in to your account"}
              </Typography>
              <Typography variant='body2' sx={{color: "#666", mb: 3, lineHeight: 1.6}}>
                {dictionary["auth"].login_subtitle || "Choose your organisation to continue securely"}
              </Typography>

              <div style={{display: "flex", flexDirection: "column", gap: 10}}>
                <Button fullWidth variant='outlined' sx={SSO_BTN} onClick={() => doOpenWebBrowser("Surfconext")}
                  startIcon={<SurfconextIcon />}>
                  {dictionary["auth"].loginSurf}
                </Button>

                <Button fullWidth variant='outlined' sx={SSO_BTN} onClick={() => doOpenWebBrowser("Kennisnet")}
                  startIcon={<KennisnetIcon />}>
                  {dictionary["auth"].loginKennis}
                </Button>

                <Button fullWidth variant='outlined' sx={SSO_BTN} onClick={doOpenAzureWebBrowser}
                  startIcon={<MicrosoftIcon />}>
                  {dictionary["auth"].loginAzure}
                </Button>
              </div>

              {error ? (
                <Alert severity='error' sx={{mt: 2}}>
                  {dictionary["auth"][error]}
                </Alert>
              ) : null}
            </div>
          </div>

          {/* Bottom */}
          <div className='login-v2-footer'>
            <Typography variant='body2' sx={{color: "#aaa", fontSize: "0.8rem"}}>
              {dictionary["auth"]?.forgotPassword || ""}
            </Typography>
          </div>
        </div>

        {/* ── RIGHT PANEL ── */}
        <div className='login-v2-right'>
          <div style={{display: "flex", flexDirection: "column", gap: 20, width: "62%"}}>
            {/* Title */}
            <Typography sx={{fontSize: "1.50rem", fontWeight: 700, color: "#1a2340"}}>
              👋 {dictionary["auth"].welcome_to} {themeConfig.templateName}!
            </Typography>

            {/* Screenshot */}
            <div style={{position: "relative", borderRadius: 20, overflow: "hidden", boxShadow: "0 16px 60px rgba(30,50,120,0.15)", border: "0px solid rgba(255,255,255,0.95)", borderRight: "0px"}}>
              <img
                src='/images/pages/prinscreen_app.png'
                alt='App preview'
                style={{width: "170%", display: "block", marginLeft: "-44%"}}
              />
              {/* Fade left — hides dark sidebar */}
              {/* <div style={{position: "absolute", top: 0, left: 0, width: "8%", height: "100%", background: "linear-gradient(to left, transparent, rgba(255,255,255,0.98))", pointerEvents: "none"}} /> */}
              {/* Fade right */}
              <div style={{position: "absolute", top: 0, right: 0, width: "32%", height: "100%", background: "linear-gradient(to right, transparent, #cdddf4)", pointerEvents: "none"}} />
              {/* Fade bottom */}
              {/* <div style={{position: "absolute", bottom: 0, left: 0, width: "100%", height: "28%", background: "linear-gradient(to bottom, transparent, #cdddf4)", pointerEvents: "none"}} /> */}
            </div>
          </div>
        </div>

      </div>

      <Loading loading={isLoading}><Text></Text></Loading>
    </>
  );
};

export default LoginV2;
